import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useUser } from '../../../hooks/useUser'
import { useQuery, useAction } from 'wasp/client/operations'
import { getUserNavPreferences, updateNavPreferences } from 'wasp/client/operations'
import { readCollapsed, writeCollapsed } from '../../../shared/navigation/collapseState'

type Role = 'user' | 'admin'

type UserNavPreferences = {
  expandedGroups: string[]
  preferredRole: Role
  lastActiveItem: string
}

type NavState = {
  expandedGroups: string[]
  activeItem: string
  userRole: Role
  navigationHistory: string[]
  isCollapsed: boolean
  dockVariant: 'favorites' | 'shopping' | 'more'
  adminMode: boolean
}

type NavContext = NavState & {
  toggleGroup: (groupId: string) => void
  setActiveItem: (itemId: string) => void
  switchRole: (role: Role) => void
  setIsCollapsed: (collapsed: boolean) => void
  saveNavigationState: () => void
  setDockVariant: (v: 'favorites' | 'shopping' | 'more') => void
  setAdminMode: (on: boolean) => void
}

const NavigationCtx = createContext<NavContext | null>(null)

// Accessibility helper function
const enhanceNavItemAccessibility = (item: string, role: Role) => {
  return `${item}-${role}-nav-item`
}

const STORAGE_KEYS = {
  expanded: 'nura_nav_expanded_groups',
  active: 'nura_nav_active_item',
  role: 'nura_nav_user_role',
  collapsed: 'nura_nav_collapsed',
  dockVariant: 'nura.nav.mobileDockVariant:v1'
}

export function NavigationProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useUser()
  
  // Server state
  const { data: serverPrefs, isLoading: isLoadingPrefs } = useQuery(
    getUserNavPreferences, 
    undefined, 
    { enabled: isAuthenticated }
  )
  const updateNavPrefsMutation = useAction(updateNavPreferences)
  
  // Debounce ref for server saves
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [isInitialized, setIsInitialized] = useState(false)

  // Get initial state from localStorage
  const getLocalStorageDefaults = useCallback(() => {
    try {
      const expandedGroups = (() => {
        const raw = localStorage.getItem(STORAGE_KEYS.expanded)
        return raw ? (JSON.parse(raw) as string[]) : ['core']
      })()
      const activeItem = localStorage.getItem(STORAGE_KEYS.active) || 'dashboard'
      const userRole = (() => {
        const v = localStorage.getItem(STORAGE_KEYS.role) as Role | null
        return v === 'admin' ? 'admin' : 'user'
      })()
      const isCollapsed = readCollapsed()
      const dockVariant = (localStorage.getItem(STORAGE_KEYS.dockVariant) as 'favorites' | 'shopping' | 'more' | null) || 'favorites'
      const adminMode = false
      return { expandedGroups, activeItem, userRole, isCollapsed, dockVariant, adminMode }
    } catch {
      return { expandedGroups: ['core'], activeItem: 'dashboard', userRole: 'user' as Role, isCollapsed: false, dockVariant: 'favorites', adminMode: false }
    }
  }, [])

  // State initialization with merged preferences
  const [expandedGroups, setExpandedGroups] = useState<string[]>(() => {
    return getLocalStorageDefaults().expandedGroups
  })
  
  const [activeItem, setActiveItemState] = useState<string>(() => {
    return getLocalStorageDefaults().activeItem
  })
  
  const [userRole, setUserRole] = useState<Role>(() => {
    return getLocalStorageDefaults().userRole as Role
  })
  
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    return getLocalStorageDefaults().isCollapsed
  })
  const [dockVariant, setDockVariantState] = useState<'favorites' | 'shopping' | 'more'>(
    () => getLocalStorageDefaults().dockVariant as 'favorites' | 'shopping' | 'more'
  )
  const [adminMode, setAdminModeState] = useState<boolean>(false)
  
  const [navigationHistory, setNavigationHistory] = useState<string[]>([])

  // Initialize state from server preferences when available
  useEffect(() => {
    if (!isAuthenticated) {
      setIsInitialized(true)
      return
    }

    if (serverPrefs && !isLoadingPrefs && !isInitialized) {
      // Merge server preferences with current state
      setExpandedGroups(serverPrefs.expandedGroups)
      setActiveItemState(serverPrefs.lastActiveItem)
      setUserRole(serverPrefs.preferredRole)
      setIsInitialized(true)
    }
  }, [serverPrefs, isLoadingPrefs, isAuthenticated, isInitialized])

  // Handle auth state changes
  useEffect(() => {
    if (!isAuthenticated && isInitialized) {
      // User logged out - reset to localStorage defaults
      const localDefaults = getLocalStorageDefaults()
      setExpandedGroups(localDefaults.expandedGroups)
      setActiveItemState(localDefaults.activeItem)
      setUserRole(localDefaults.userRole as Role)
    }
    setIsInitialized(!isAuthenticated)
  }, [isAuthenticated, getLocalStorageDefaults, isInitialized])

  // Debounced server save function
  const debouncedServerSave = useCallback(
    (prefs: Partial<UserNavPreferences>) => {
      if (!isAuthenticated) return
      
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
      
      saveTimeoutRef.current = setTimeout(async () => {
        try {
          await updateNavPrefsMutation(prefs)
        } catch (error) {
          console.error('Failed to save navigation preferences:', error)
        }
      }, 1500) // 1.5 second debounce
    },
    [isAuthenticated, updateNavPrefsMutation]
  )

  const toggleGroup = useCallback((groupId: string) => {
    setExpandedGroups(prev => {
      const newGroups = prev.includes(groupId) 
        ? prev.filter(id => id !== groupId) 
        : [...prev, groupId]
      
      // Save to server if authenticated
      debouncedServerSave({ expandedGroups: newGroups })
      
      return newGroups
    })
  }, [debouncedServerSave])

  const setActiveItem = useCallback((itemId: string) => {
    setActiveItemState(itemId)
    setNavigationHistory(h => [...h.slice(-49), itemId])
    
    // Save to server if authenticated
    debouncedServerSave({ lastActiveItem: itemId })
  }, [debouncedServerSave])

  const switchRole = useCallback((role: Role) => {
    setUserRole(role)
    
    // Save to server if authenticated
    debouncedServerSave({ preferredRole: role })
  }, [debouncedServerSave])

  const saveNavigationState = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.expanded, JSON.stringify(expandedGroups))
      localStorage.setItem(STORAGE_KEYS.active, activeItem)
      localStorage.setItem(STORAGE_KEYS.role, userRole)
      writeCollapsed(isCollapsed)
      localStorage.setItem(STORAGE_KEYS.dockVariant, dockVariant)
    } catch {}
  }, [expandedGroups, activeItem, userRole, isCollapsed, dockVariant])

  const setDockVariant = useCallback((v: 'favorites' | 'shopping' | 'more') => {
    setDockVariantState(v)
    try { localStorage.setItem(STORAGE_KEYS.dockVariant, v) } catch {}
  }, [])

  const setAdminMode = useCallback((on: boolean) => {
    setAdminModeState(!!on)
  }, [])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => { saveNavigationState() }, [saveNavigationState])

  const value = useMemo<NavContext>(() => ({
    expandedGroups,
    activeItem,
    userRole,
    navigationHistory,
    isCollapsed,
    dockVariant,
    adminMode,
    toggleGroup,
    setActiveItem,
    switchRole,
    setIsCollapsed,
    saveNavigationState,
    setDockVariant,
    setAdminMode,
  }), [expandedGroups, activeItem, userRole, navigationHistory, isCollapsed, dockVariant, adminMode, toggleGroup, setActiveItem, switchRole, setIsCollapsed, saveNavigationState])

  return (
    <NavigationCtx.Provider value={value}>{children}</NavigationCtx.Provider>
  )
}

export function useNavigation() {
  const ctx = useContext(NavigationCtx)
  if (!ctx) throw new Error('useNavigation must be used within NavigationProvider')
  return ctx
}
